/**
 * `<SoftwareUpdateSettings>` — desktop-only Settings card to see the current app
 * version and check for / install updates on demand (2026-05-27).
 *
 * The automatic, launch-time check lives in `<DesktopUpdateWatcher>` (mounted at
 * the app root); this card is the manual escape hatch — "am I current?" + a
 * "check now" button — and shows download progress when installing. Renders
 * nothing on the web build (`isDesktopApp()` gate).
 *
 * Both paths share the low-level bridge in `@/desktop/updater`.
 */

import { useEffect, useState } from 'react';
import { SettingsCard } from './SettingsCard';
import { Button } from '@/components/ui';
import { isDesktopApp } from '@/desktop/isDesktopApp';
import { openDevtools } from '@/desktop/desktopIpc';
import {
  checkForUpdate,
  installUpdateAndRelaunch,
  getAppVersion,
  type AvailableUpdate,
} from '@/desktop/updater';

type Phase = 'idle' | 'checking' | 'uptodate' | 'available' | 'installing' | 'error';

export function SoftwareUpdateSettings(): JSX.Element | null {
  const desktop = isDesktopApp();

  const [version, setVersion] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [update, setUpdate] = useState<AvailableUpdate | null>(null);
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!desktop) return;
    void getAppVersion().then(setVersion);
  }, [desktop]);

  // Web build: the whole MCP/desktop story is irrelevant — render nothing.
  if (!desktop) return null;

  const onCheck = async (): Promise<void> => {
    setPhase('checking');
    setErrorMsg(null);
    const found = await checkForUpdate();
    if (found) {
      setUpdate(found);
      setPhase('available');
    } else {
      setPhase('uptodate');
    }
  };

  const onInstall = async (): Promise<void> => {
    if (!update) return;
    setPhase('installing');
    setProgress(0);
    try {
      await installUpdateAndRelaunch(update, setProgress);
      // App relaunches on success — control never returns here.
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
      setPhase('error');
    }
  };

  return (
    <SettingsCard
      id="software-update"
      title="Software update"
      desc="Aletheia updates itself in place — no reinstall. The web view always loads the latest; this updates the native shell."
    >
      <div className="mcp-section">
        <p className="mcp-hint">
          Current version: <code>{version ?? '…'}</code>
        </p>

        <div className="mcp-row" style={{ alignItems: 'center', gap: 8 }}>
          <Button
            variant="default"
            onClick={() => void onCheck()}
            disabled={phase === 'checking' || phase === 'installing'}
          >
            {phase === 'checking' ? 'Checking…' : 'Check for updates'}
          </Button>

          {phase === 'available' && update && (
            <Button variant="outline" onClick={() => void onInstall()}>
              Install {update.version} &amp; restart
            </Button>
          )}

          {phase === 'uptodate' && (
            <span className="mcp-success">✓ You're on the latest version.</span>
          )}
          {phase === 'available' && update && (
            <span className="mcp-hint">Version {update.version} is available.</span>
          )}
          {phase === 'installing' && (
            <span className="mcp-hint">
              Downloading… {Math.round(progress * 100)}% — the app will restart.
            </span>
          )}
          {phase === 'error' && (
            <span className="mcp-error">Update failed{errorMsg ? `: ${errorMsg}` : ''}.</span>
          )}
        </div>

        {/* The chromeless window has no menu / F12, so expose the inspector here.
            Inside DevTools you can hard-reload (Ctrl+Shift+R) or clear the cache —
            useful when the web view is pinned to a stale deployment. */}
        <div className="mcp-row" style={{ alignItems: 'center', gap: 8, marginTop: 8 }}>
          <Button variant="outline" onClick={() => void openDevtools()}>
            Open developer tools
          </Button>
          <span className="mcp-hint">
            Inspector for the web view. Use Ctrl+Shift+R inside it to hard-reload past a stale cache.
          </span>
        </div>
      </div>
    </SettingsCard>
  );
}
